import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import './ReviewQueue.css'

export default function ReviewQueue() {
  const { user, profile } = useAuth()
  const [queue, setQueue] = useState([])
  const [loading, setLoading] = useState(true)
  const [active, setActive] = useState(null) // project currently being reviewed
  const [commits, setCommits] = useState([])
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => { if (user) fetchQueue() }, [user]) // eslint-disable-line

  async function fetchQueue() {
    setLoading(true)
    const { data } = await supabase
      .from('projects')
      .select('*, profiles!projects_user_id_fkey(display_name, full_name, username)')
      .eq('status', 'submitted')
      .neq('user_id', user.id)
      .order('submitted_at', { ascending: true })
      .limit(30)

    // hide projects this reviewer already reviewed
    const { data: mine } = await supabase.from('reviews').select('project_id').eq('reviewer_id', user.id)
    const reviewedIds = new Set((mine || []).map(r => r.project_id))
    setQueue((data || []).filter(p => !reviewedIds.has(p.id)))
    setLoading(false)
  }

  async function openProject(project) {
    setActive(project)
    setNote('')
    const { data } = await supabase
      .from('commits')
      .select('*')
      .eq('project_id', project.id)
      .order('created_at', { ascending: true })
    setCommits(data || [])
  }

  async function castVerdict(verdict) {
    if (!active || submitting) return
    setSubmitting(true)
    const { error } = await supabase.from('reviews').insert({
      project_id: active.id,
      reviewer_id: user.id,
      verdict,
      note: note || null,
    })
    setSubmitting(false)
    if (error) { alert(error.message); return }
    setActive(null)
    fetchQueue()
  }

  if (!user) {
    return (
      <div className="container" style={{ padding: '60px 24px', textAlign: 'center' }}>
        <p>Sign in to review projects.</p>
        <Link to="/login" className="btn btn-primary" style={{ marginTop: 16 }}>Sign in</Link>
      </div>
    )
  }

  return (
    <div className="review-page page-fade">
      <div className="container container-narrow">
        <div className="review-header">
          <h1>Review queue</h1>
          <p>Look at the full commit trail and judge whether it's genuinely human-made. You've reviewed {profile?.reviews_completed_count || 0} project{profile?.reviews_completed_count === 1 ? '' : 's'} so far — review 2 before you can submit your own.</p>
        </div>

        {active ? (
          <div className="review-detail">
            <button className="btn btn-secondary" onClick={() => setActive(null)} style={{ marginBottom: 16 }}>← Back to queue</button>
            <h2>{active.title}</h2>
            <p className="review-detail-meta">
              by {active.profiles?.display_name || active.profiles?.full_name || 'Anonymous'} · {active.category}
            </p>
            {active.description && <p className="review-detail-desc">{active.description}</p>}

            <div className="review-commit-trail">
              {commits.length === 0 && <p className="review-empty-note">No commits logged for this project.</p>}
              {commits.map(c => (
                <div key={c.id} className="review-commit-row">
                  <span className="review-commit-date">{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  <span className="review-commit-msg">{c.message}</span>
                  {c.media_url && <a href={c.media_url} target="_blank" rel="noreferrer" className="review-commit-media">view media</a>}
                </div>
              ))}
            </div>

            <textarea
              placeholder="Optional note explaining your verdict"
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              style={{ marginTop: 16, marginBottom: 12 }}
            />

            <div className="review-verdict-actions">
              <button className="btn verdict-genuine" onClick={() => castVerdict('genuine')} disabled={submitting}>Looks genuine</button>
              <button className="btn verdict-needs" onClick={() => castVerdict('needs_evidence')} disabled={submitting}>Needs more evidence</button>
              <button className="btn verdict-suspicious" onClick={() => castVerdict('suspicious')} disabled={submitting}>Suspicious</button>
            </div>
          </div>
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><span className="loading-spinner" /></div>
        ) : queue.length === 0 ? (
          <div className="wall-empty">
            <h3>Nothing to review right now</h3>
            <p>Check back soon — new submissions will show up here.</p>
          </div>
        ) : (
          <div className="review-queue-list">
            {queue.map(p => (
              <button key={p.id} className="review-queue-item" onClick={() => openProject(p)}>
                <span className="review-queue-title">{p.title}</span>
                <span className="review-queue-sub">{p.category} · by {p.profiles?.display_name || p.profiles?.full_name || 'Anonymous'}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
